/**
 * Flexer Server - WebSocket Service
 * Gerçek zamanlı veri iletişimi için WebSocket bağlantısı
 */

import logger from './logger';

class WebSocketService {
  constructor() {
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = Infinity; // Sonsuz reconnect denemesi
    this.reconnectDelay = 1000; // 1 second initial delay
    this.maxReconnectDelay = 60000; // 60 seconds maximum delay (exponential backoff)
    this.listeners = new Map();
    this.isConnected = false;
    this.isConnecting = false; // Bağlantı kurulurken flag
    this.connectionPromise = null; // Bağlantı promise'i

    // Heartbeat/Ping-Pong mechanism
    this.heartbeatInterval = null;
    this.heartbeatTimeout = null;
    this.pingIntervalMs = 30000; // 30 saniyede bir ping gönder
    this.pongTimeoutMs = 5000; // 5 saniye içinde pong bekle
    this.lastPongTime = null;

    // Page Visibility API for battery optimization
    this.isPageVisible = true;
    this.setupPageVisibilityListener();

    // Status change callbacks (event-based status updates)
    this.statusChangeCallbacks = [];
  }

  /**
   * Status değişikliği için callback kaydet
   * Layout component'i bu callback ile gerçek zamanlı status güncellemesi alır
   */
  onStatusChange(callback) {
    this.statusChangeCallbacks.push(callback);
    // Cleanup function döndür
    return () => {
      const index = this.statusChangeCallbacks.indexOf(callback);
      if (index > -1) {
        this.statusChangeCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Status değişikliğini tüm listener'lara bildir
   */
  notifyStatusChange() {
    const status = this.getConnectionStatus();
    this.statusChangeCallbacks.forEach(callback => {
      try {
        callback(status);
      } catch (error) {
        logger.error('Status change callback error:', error);
      }
    });
  }

  /**
   * Page Visibility API listener - Tab hidden olduğunda ping'i durdur
   * Battery optimization için kritik
   */
  setupPageVisibilityListener() {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        this.isPageVisible = !document.hidden;

        if (this.isPageVisible) {
          logger.debug('🔍 Page visible - Heartbeat devam ediyor');
          // Tab visible olunca hemen ping gönder
          if (this.isConnected) {
            this.sendPing();
          }
        } else {
          logger.debug('🔍 Page hidden - Heartbeat battery optimization mode');
          // Timeout'ları temizle ama interval'ı çalıştırmaya devam et
          // (Sadece ping göndermeyeceğiz)
          this.clearPongTimeout();
        }
      });
    }
  }

  async connect(token) {
    try {
      logger.debug('🔍 WebSocketService.connect() çağrıldı');

      // Token kontrolü
      if (!token) {
        logger.warn('WebSocket bağlantısı için token gerekli');
        return;
      }

      // Eğer zaten bağlıysa, yeni bağlantı kurma
      if (this.ws && this.isConnected) {
        logger.debug('🔍 WebSocket zaten bağlı');
        return;
      }

      // Eğer bağlantı kuruluyorsa, mevcut promise'i bekle
      if (this.isConnecting && this.connectionPromise) {
        logger.debug('🔍 WebSocket bağlantısı zaten kuruluyor');
        return this.connectionPromise;
      }

      // Bağlantı kuruluyor flag'i set et
      this.isConnecting = true;

      // WebSocket URL'ini oluştur - Environment-aware
      logger.debug('🔍 WebSocket - REACT_APP_WS_URL:', process.env.REACT_APP_WS_URL);

      // Fallback: Use current hostname and protocol
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const hostname = window.location.hostname;
      const wsUrl = process.env.REACT_APP_WS_URL || `${protocol}//${hostname}:8000/ws`;

      // SECURITY FIX: Token'ı URL query parameter yerine WebSocket protokol header'ında gönder
      // Bu yöntem token'ın URL'de görünmesini ve loglanmasını önler
      // Fallback olarak query parameter da desteklenir (backward compatibility)
      const tokenProtocol = `token.${token}`;

      logger.debug('🔍 WebSocket - Base URL:', wsUrl);
      logger.debug('🔍 WebSocket - Using secure header-based authentication');

      // Bağlantı promise'i oluştur
      this.connectionPromise = new Promise((resolve) => {
        // WebSocket bağlantısını token protokolü ile kur
        this.ws = new WebSocket(wsUrl, [tokenProtocol]);

        this.ws.onopen = () => {
          logger.info('🔌 WebSocket bağlantısı kuruldu (secure mode)');
          this.isConnected = true;
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          this.lastPongTime = Date.now();

          // Heartbeat mekanizmasını başlat
          this.startHeartbeat();

          // Status değişikliğini bildir (event-based)
          this.notifyStatusChange();

          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            this.handleMessage(data);
          } catch (error) {
            logger.error('WebSocket mesaj parse hatası:', error);
          }
        };

        this.ws.onclose = (event) => {
          logger.info('🔌 WebSocket bağlantısı kapandı:', event.code, event.reason);
          this.isConnected = false;
          this.isConnecting = false;
          this.connectionPromise = null;

          // Heartbeat mekanizmasını durdur
          this.stopHeartbeat();

          // Status değişikliğini bildir (event-based)
          this.notifyStatusChange();

          this.handleReconnect();
        };

        this.ws.onerror = (error) => {
          // Runtime overlay hatalarını tetiklememek için reject etmeyelim
          logger.error('WebSocket hatası:', error);
          this.isConnected = false;
          this.isConnecting = false;
          // connectionPromise'i reject etmiyoruz; reconnect akışına bırakıyoruz
        };
      });
      
      return this.connectionPromise;

    } catch (error) {
      logger.error('WebSocket bağlantı hatası:', error);
      this.isConnecting = false;
      this.connectionPromise = null;
      this.handleReconnect();
      // Hata fırlatma yok; UI'yı çökertmeyelim
      return;
    }
  }

  handleReconnect() {
    this.reconnectAttempts++;
    this.isConnecting = true;

    // Exponential backoff: 1s → 2s → 4s → 8s → ... → max 60s
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectDelay
    );

    logger.info(
      `WebSocket yeniden bağlanma denemesi #${this.reconnectAttempts} - ${delay}ms sonra`
    );

    // Status değişikliğini bildir (connecting state)
    this.notifyStatusChange();

    setTimeout(() => {
      // Token'ı localStorage'dan al
      const token = localStorage.getItem('token');
      if (token) {
        this.connect(token);
      } else {
        logger.error('Token bulunamadı, yeniden bağlanma iptal edildi');
        this.isConnecting = false;
        this.notifyStatusChange();
      }
    }, delay);
  }

  handleMessage(data) {
    logger.debug('🔍 WebSocket raw message - Type:', data.type, 'Full data:', data);

    // Pong mesajını yakala (heartbeat response)
    if (data.type === 'pong') {
      this.lastPongTime = Date.now();
      this.clearPongTimeout();
      logger.debug('💓 Pong received - Connection healthy');
      return;
    }

    // Telemetri mesajı için özel log
    if (data.type === 'telemetry') {
      logger.info('📡 TELEMETRY MESSAGE RECEIVED:', {
        device_id: data.device_id,
        device_status: data.device_status,
        last_seen: data.last_seen,
        sensors: data.data?.sensors,
        status: data.data?.status,
        device_info: data.device_info
      });
    }

    // Device status mesajı için özel log
    if (data.type === 'device_status') {
      logger.info('📡 DEVICE STATUS MESSAGE RECEIVED:', {
        device_id: data.device_id,
        status: data.status,
        last_seen: data.last_seen,
        data: data.data
      });
    }

    const { type } = data;

    // Mesaj tipine göre listener'ları çağır
    if (this.listeners.has(type)) {
      logger.debug(`🔍 Calling ${this.listeners.get(type).length} listeners for type: ${type}`);
      this.listeners.get(type).forEach((callback, index) => {
        try {
          logger.debug(`🔍 Executing callback ${index + 1} for type: ${type}`);
          // Backend'den gelen mesajı olduğu gibi geçir
          callback(data);
        } catch (error) {
          logger.error(`WebSocket listener hatası (${type}):`, error);
        }
      });
    } else {
      // Ignore system messages that don't need listeners (ping, pong, connection_established)
      const systemMessages = ['ping', 'pong', 'connection_established'];
      if (!systemMessages.includes(type)) {
        logger.warn(`⚠️ No listeners registered for type: ${type}`);
      }
    }
  }

  subscribe(type, callback) {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, []);
    }
    this.listeners.get(type).push(callback);
    
    // Cleanup function döndür
    return () => {
      const callbacks = this.listeners.get(type);
      if (callbacks) {
        const index = callbacks.indexOf(callback);
        if (index > -1) {
          callbacks.splice(index, 1);
        }
      }
    };
  }

  send(type, payload) {
    if (this.isConnected && this.ws) {
      try {
        this.ws.send(JSON.stringify({ type, payload }));
      } catch (error) {
        logger.error('WebSocket gönderme hatası:', error);
      }
    } else {
      logger.warn('WebSocket bağlantısı yok, mesaj gönderilemedi');
    }
  }

  disconnect() {
    logger.debug('🔍 WebSocketService.disconnect() çağrıldı, mevcut bağlantı:', this.ws ? 'var' : 'yok');

    // Heartbeat mekanizmasını durdur
    this.stopHeartbeat();

    if (this.ws) {
      this.ws.close();
      this.ws = null;
      this.isConnected = false;
      this.isConnecting = false;
      this.connectionPromise = null;
      this.reconnectAttempts = 0; // Reconnect counter'ı sıfırla
      logger.info('🔌 WebSocket bağlantısı kapatıldı');
    }
  }

  // Özel mesaj tipleri için yardımcı metodlar
  subscribeToDeviceStatus(callback) {
    return this.subscribe('device_status', callback);
  }

  subscribeToTelemetry(callback) {
    return this.subscribe('telemetry', callback);
  }

  subscribeToSystemStatus(callback) {
    return this.subscribe('system_status', callback);
  }

  // Cihaz kontrolü için mesaj gönderme
  sendDeviceCommand(deviceId, command, params = {}) {
    this.send('device_command', {
      device_id: deviceId,
      command,
      params
    });
  }

  /**
   * Heartbeat mekanizmasını başlat
   * Her 30 saniyede bir ping gönder
   */
  startHeartbeat() {
    // Önceki interval'ı temizle
    this.stopHeartbeat();

    logger.info('💓 Heartbeat başlatıldı (30s interval)');

    // İlk ping'i hemen gönder
    this.sendPing();

    // Periyodik ping gönderimi
    this.heartbeatInterval = setInterval(() => {
      this.sendPing();
    }, this.pingIntervalMs);
  }

  /**
   * Heartbeat mekanizmasını durdur
   */
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
      logger.debug('💓 Heartbeat durduruldu');
    }

    this.clearPongTimeout();
  }

  /**
   * Pong timeout'unu temizle
   */
  clearPongTimeout() {
    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
      this.heartbeatTimeout = null;
    }
  }

  /**
   * Ping gönder ve pong timeout'u başlat
   * Page hidden durumunda ping gönderme (battery optimization)
   */
  sendPing() {
    // Page hidden durumunda ping gönderme (battery save)
    if (!this.isPageVisible) {
      logger.debug('💓 Ping skipped - Page hidden (battery optimization)');
      return;
    }

    if (!this.isConnected || !this.ws) {
      logger.warn('💓 Ping skipped - WebSocket not connected');
      return;
    }

    // Ping gönder
    this.send('ping', { timestamp: Date.now() });
    logger.debug('💓 Ping sent');

    // Pong timeout başlat (5 saniye)
    this.clearPongTimeout();
    this.heartbeatTimeout = setTimeout(() => {
      logger.error('💓 Pong timeout - WebSocket connection unhealthy, reconnecting...');

      // Bağlantıyı kapat (onclose otomatik reconnect'i tetikleyecek)
      if (this.ws) {
        this.ws.close();
      }
    }, this.pongTimeoutMs);
  }

  // Ping gönderme (backward compatibility - eski method)
  ping() {
    this.sendPing();
  }

  // Bağlantı durumunu döndür
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      isConnecting: this.isConnecting,
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts
    };
  }
}

// Singleton instance
const websocketService = new WebSocketService();

export default websocketService;
